import { useQuery, useMutation, useInfiniteQuery } from 'react-query';

import { IResponse, api, IIndexSignature } from '@selfserve/react-utils';

export type IUseFetchParams = IIndexSignature<
  string | number | boolean | string[] | number[]
>;

export const useFetch = <T>(
  url: string | null,
  params?: IUseFetchParams,
  config?: object,
  noCors?: boolean
) => {
  return useQuery<IResponse<T>>(
    [url!, params],
    ({ queryKey }) => {
      const [url, params] = queryKey;
      return api.get<T>(url as string, { ...(params as object) }, noCors);
    },
    {
      ...config,
    }
  );
};

export const useInfiniteFetch = <T>(
  url: string | null,
  pageKey: string,
  params?: IUseFetchParams,
  config?: object
) => {
  return useInfiniteQuery<IResponse<T>>(
    [url!, params],
    ({ queryKey, pageParam = 0 }) => {
      const [url, params] = queryKey;
      return api.get<T>(url as string, {
        ...(params as object),
        ...(pageKey && {
          [pageKey]: pageParam,
        }),
      });
    },
    {
      getNextPageParam: (lastPage, pages) => pages.length,
      ...config,
    }
  );
};

export const usePost = <T, S>(
  url: string,
  params?: object,
  config?: object
) => {
  return useMutation<IResponse<S>, unknown, T>(
    (data): Promise<IResponse<S>> => api.post<T>(url, data, params),
    {
      ...config,
      mutationKey: url,
    }
  );
};

export const usePatch = <T, S>(
  url: string,
  params?: object,
  config?: object
) => {
  return useMutation<IResponse<S>, unknown, T>(
    (data): Promise<IResponse<S>> => api.patch<T>(url, data, params),
    {
      ...config,
      mutationKey: url,
    }
  );
};

export const usePut = <T, S>(url: string, params?: object, config?: object) => {
  return useMutation<IResponse<S>, unknown, T>(
    (data): Promise<IResponse<S>> => api.put<T>(url, data, params),
    {
      ...config,
      mutationKey: url,
    }
  );
};
